import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers,
  FiDownload,
  FiCheck,
  FiX,
  FiUserMinus,
  FiCreditCard,
  FiEye,
  FiCalendar,
  FiCheckCircle,
} from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import LoadingSpinner from "../ui/LoadingSpinner";
import "./ParticipantManagement.css";

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 },
  },
};

// Modal Component
const Modal = ({ title, children, onClose }) => {
  return (
    <motion.div
      className="wpm-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="wpm-modal"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wpm-modal-header">
          <h3 className="wpm-modal-title">{title}</h3>
          <button className="wpm-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
};

// Participant Card Component
const ParticipantCard = ({
  participant,
  userRole,
  canApprove = true,
  onApprove,
  onReject,
  onRemove,
  onApprovePayment,
  onRejectPayment,
  isSelected,
  onToggleSelection,
  showCheckbox,
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentRejectModal, setShowPaymentRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [removalReason, setRemovalReason] = useState("");
  const [paymentRejectionReason, setPaymentRejectionReason] = useState("");

  // Check if user can manage participants
  // Organizers can always manage, co-organizers need canApprove permission
  const isOrganizer = userRole === "organizer";
  const isCoOrganizer = userRole === "co-organizer";
  const canManage = isOrganizer || (isCoOrganizer && canApprove);
  const isPending = participant.status === "pending";
  const isApproved = participant.status === "approved";
  const hasPaymentProof =
    participant.paymentProof || participant.paymentProofUrl;
  const paymentStatus = participant.paymentStatus;

  const isPaymentPending =
    hasPaymentProof &&
    (paymentStatus === "pending" ||
      participant.status === "payment_pending" ||
      participant.status === "payment-pending");

  const canApproveApplication = isPending && !isPaymentPending;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      pending: "wpm-status-pending",
      approved: "wpm-status-approved",
      rejected: "wpm-status-rejected",
      payment_pending: "wpm-status-payment_pending",
      "payment-pending": "wpm-status-payment-pending",
    };
    return statusMap[status] || "wpm-status-pending";
  };

  return (
    <motion.div className="wpm-card" variants={itemVariants}>
      <div className="wpm-card-header">
        {showCheckbox && canManage && (isPending || isPaymentPending) && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelection}
            className="wpm-checkbox"
          />
        )}

        <img
          src={participant.user.profilePhotoUrl || "/default-avatar.png"}
          alt={participant.user.name}
          className="wpm-avatar"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        />
        <div className="wpm-avatar-fallback">
          {participant.user.name?.charAt(0).toUpperCase()}
        </div>

        <div className="wpm-user-info">
          <h4 className="wpm-user-name">{participant.user.name}</h4>
          <p className="wpm-user-email">{participant.user.email}</p>
          <span
            className={`wpm-status-badge ${getStatusBadgeClass(
              participant.status
            )}`}
          >
            {participant.status === "payment_pending" ||
            participant.status === "payment-pending"
              ? "Payment Pending"
              : participant.status}
          </span>
        </div>
      </div>

      {/* Details Section */}
      {(participant.appliedAt ||
        participant.motivation ||
        participant.experience) && (
        <div className="wpm-card-details">
          {participant.appliedAt && (
            <div className="wpm-detail-row">
              <span className="wpm-detail-label">
                <FiCalendar size={12} /> Applied
              </span>
              <span className="wpm-detail-value">
                {formatDate(participant.appliedAt)}
              </span>
            </div>
          )}
          {participant.motivation && (
            <div className="wpm-detail-row">
              <span className="wpm-detail-label">Motivation</span>
              <span className="wpm-detail-value">{participant.motivation}</span>
            </div>
          )}
          {participant.experience && (
            <div className="wpm-detail-row">
              <span className="wpm-detail-label">Experience</span>
              <span className="wpm-detail-value">{participant.experience}</span>
            </div>
          )}
        </div>
      )}

      {/* Payment Section */}
      {hasPaymentProof && (
        <div className="wpm-payment-section">
          <h5 className="wpm-payment-title">
            <FiCreditCard size={14} />
            Payment Information
          </h5>
          <div className="wpm-payment-details">
            {(participant.paymentMethod ||
              participant.paymentProof?.paymentMethod) && (
              <div className="wpm-payment-row">
                <span className="wpm-payment-label">Method:</span>
                <span className="wpm-payment-value">
                  {participant.paymentMethod ||
                    participant.paymentProof?.paymentMethod}
                </span>
              </div>
            )}
            {(participant.transactionId ||
              participant.paymentProof?.transactionId) && (
              <div className="wpm-payment-row">
                <span className="wpm-payment-label">Transaction:</span>
                <span className="wpm-payment-value">
                  {participant.transactionId ||
                    participant.paymentProof?.transactionId}
                </span>
              </div>
            )}
          </div>
          <button
            className="wpm-view-proof-btn"
            onClick={() => setShowPaymentModal(true)}
          >
            <FiEye />
            View Payment Proof
          </button>
        </div>
      )}

      {/* Actions */}
      {canManage && (
        <div className="wpm-card-actions">
          {canApproveApplication && (
            <>
              <button className="wpm-btn wpm-btn-success" onClick={onApprove}>
                <FiCheck />
                Approve
              </button>
              <button
                className="wpm-btn wpm-btn-danger"
                onClick={() => setShowRejectModal(true)}
              >
                <FiX />
                Reject
              </button>
            </>
          )}

          {isPaymentPending && (
            <>
              <button
                className="wpm-btn wpm-btn-success"
                onClick={() =>
                  onApprovePayment("Payment verified successfully")
                }
              >
                <FiCheck />
                Approve Payment
              </button>
              <button
                className="wpm-btn wpm-btn-danger"
                onClick={() => setShowPaymentRejectModal(true)}
              >
                <FiX />
                Reject Payment
              </button>
            </>
          )}

          {isApproved && (
            <button
              className="wpm-btn wpm-btn-warning"
              onClick={() => setShowRemoveModal(true)}
            >
              <FiUserMinus />
              Remove
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showRejectModal && (
          <Modal
            title="Reject Application"
            onClose={() => setShowRejectModal(false)}
          >
            <div className="wpm-modal-body">
              <p className="wpm-modal-text">
                Please provide a reason for rejecting this application.
              </p>
              <textarea
                className="wpm-modal-textarea"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={4}
              />
              <div className="wpm-modal-actions">
                <button
                  className="wpm-btn wpm-btn-danger"
                  onClick={() => {
                    if (!rejectionReason.trim()) {
                      alert("Please provide a reason");
                      return;
                    }
                    onReject(rejectionReason);
                    setShowRejectModal(false);
                    setRejectionReason("");
                  }}
                  disabled={!rejectionReason.trim()}
                >
                  Reject
                </button>
                <button
                  className="wpm-btn wpm-btn-secondary"
                  onClick={() => setShowRejectModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}

        {showRemoveModal && (
          <Modal
            title="Remove Participant"
            onClose={() => setShowRemoveModal(false)}
          >
            <div className="wpm-modal-body">
              <p className="wpm-modal-text">
                Are you sure you want to remove{" "}
                <strong>{participant.user.name}</strong> from this workshop?
              </p>
              <textarea
                className="wpm-modal-textarea"
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value)}
                placeholder="Reason for removal (optional)..."
                rows={3}
              />
              <div className="wpm-modal-actions">
                <button
                  className="wpm-btn wpm-btn-warning"
                  onClick={() => {
                    onRemove(removalReason);
                    setShowRemoveModal(false);
                    setRemovalReason("");
                  }}
                >
                  Remove
                </button>
                <button
                  className="wpm-btn wpm-btn-secondary"
                  onClick={() => setShowRemoveModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}

        {showPaymentModal && hasPaymentProof && (
          <Modal
            title="Payment Proof"
            onClose={() => setShowPaymentModal(false)}
          >
            <div className="wpm-modal-body wpm-proof-modal">
              <img
                src={
                  participant.paymentProof?.signedUrl ||
                  participant.paymentProof?.imageUrl ||
                  participant.paymentProofUrl
                }
                alt="Payment proof"
                className="wpm-proof-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "block";
                }}
              />
              <p className="wpm-proof-fallback">
                Payment proof image could not be loaded.
              </p>
              <div className="wpm-proof-details">
                <div className="wpm-proof-row">
                  <span className="wpm-proof-label">Method:</span>
                  <span className="wpm-proof-value">
                    {participant.paymentMethod ||
                      participant.paymentProof?.paymentMethod ||
                      "Not specified"}
                  </span>
                </div>
                <div className="wpm-proof-row">
                  <span className="wpm-proof-label">Transaction ID:</span>
                  <span className="wpm-proof-value">
                    {participant.transactionId ||
                      participant.paymentProof?.transactionId ||
                      "Not provided"}
                  </span>
                </div>
                <div className="wpm-proof-row">
                  <span className="wpm-proof-label">Status:</span>
                  <span className="wpm-proof-value">
                    {participant.paymentStatus ||
                      participant.paymentProof?.status ||
                      "Unknown"}
                  </span>
                </div>
                {participant.paymentProof?.uploadedAt && (
                  <div className="wpm-proof-row">
                    <span className="wpm-proof-label">Uploaded:</span>
                    <span className="wpm-proof-value">
                      {new Date(
                        participant.paymentProof.uploadedAt
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {participant.notes && (
                  <div className="wpm-proof-notes">
                    <p className="wpm-proof-notes-title">Application Notes:</p>
                    <p className="wpm-proof-notes-text">{participant.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </Modal>
        )}

        {showPaymentRejectModal && (
          <Modal
            title="Reject Payment"
            onClose={() => setShowPaymentRejectModal(false)}
          >
            <div className="wpm-modal-body">
              <p className="wpm-modal-text">
                Please provide a reason for rejecting this payment proof.
              </p>
              <textarea
                className="wpm-modal-textarea"
                value={paymentRejectionReason}
                onChange={(e) => setPaymentRejectionReason(e.target.value)}
                placeholder="Enter payment rejection reason..."
                rows={4}
              />
              <div className="wpm-modal-actions">
                <button
                  className="wpm-btn wpm-btn-danger"
                  onClick={() => {
                    if (!paymentRejectionReason.trim()) {
                      alert("Please provide a reason");
                      return;
                    }
                    onRejectPayment(
                      paymentRejectionReason,
                      `Payment rejected: ${paymentRejectionReason}`
                    );
                    setShowPaymentRejectModal(false);
                    setPaymentRejectionReason("");
                  }}
                  disabled={!paymentRejectionReason.trim()}
                >
                  Reject Payment
                </button>
                <button
                  className="wpm-btn wpm-btn-secondary"
                  onClick={() => setShowPaymentRejectModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Component
const ParticipantManagement = ({ workshopId: propWorkshopId, userRole = "organizer", canApprove = true }) => {
  const params = useParams();
  // Support both prop-based (from PersistentWorkshopsLayout) and route-based workshopId
  const workshopId = propWorkshopId || params.workshopId;
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedParticipants, setSelectedParticipants] = useState([]);

  useEffect(() => {
    fetchParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopId, filter]);

  const fetchParticipants = async () => {
    try {
      setLoading(true);
      const response = await workshopApi.getWorkshopParticipants(workshopId, {
        status: filter === "all" ? "" : filter,
      });
      if (response.success) {
        setParticipants(response.data.participants || []);
      }
    } catch (err) {
      setError(err.message);
} finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      const result = await workshopApi.approveParticipantApplication(
        workshopId,
        userId
      );
      if (result.success) {
        if (result.data?.participants) {
          setParticipants(result.data.participants);
        } else {
          fetchParticipants();
        }
      }
    } catch (error) {
alert("Failed to approve participant. Please try again.");
    }
  };

  const handleReject = async (userId, reason) => {
    try {
      const result = await workshopApi.rejectParticipantApplication(
        workshopId,
        userId,
        {
          reason,
          message: reason,
        }
      );
      if (result.success) {
        if (result.data?.participants) {
          setParticipants(result.data.participants);
        } else {
          fetchParticipants();
        }
      }
    } catch (error) {
alert("Failed to reject participant. Please try again.");
    }
  };

  const handleRemove = async (userId, reason) => {
    try {
      const result = await workshopApi.removeParticipant(workshopId, userId, {
        reason,
      });
      if (result.success) {
        if (result.data?.participants) {
          setParticipants(result.data.participants);
        } else {
          fetchParticipants();
        }
      }
    } catch (error) {
alert(`Failed to remove participant: ${error.message}`);
    }
  };

  const handleApprovePayment = async (userId, message) => {
    try {
      const participant = participants.find((p) => p.user._id === userId);
      if (
        !participant ||
        (!participant.paymentProof && !participant.paymentProofUrl)
      ) {
        alert("No payment proof found");
        return;
      }
      const result = await workshopApi.approvePayment(workshopId, userId, {
        message,
      });
      if (result.success) {
        if (result.data?.participants) {
          setParticipants(result.data.participants);
        } else {
          fetchParticipants();
        }
      }
    } catch (error) {
alert("Failed to approve payment. Please try again.");
    }
  };

  const handleRejectPayment = async (userId, reason, message) => {
    try {
      const participant = participants.find((p) => p.user._id === userId);
      if (
        !participant ||
        (!participant.paymentProof && !participant.paymentProofUrl)
      ) {
        alert("No payment proof found");
        return;
      }
      const result = await workshopApi.rejectPayment(workshopId, userId, {
        reason,
        message,
      });
      if (result.success) {
        if (result.data?.participants) {
          setParticipants(result.data.participants);
        } else {
          fetchParticipants();
        }
      }
    } catch (error) {
alert("Failed to reject payment. Please try again.");
    }
  };

  const handleBulkApprove = async () => {
    if (selectedParticipants.length === 0) {
      alert("Please select participants to approve");
      return;
    }
    try {
      const result = await workshopApi.bulkApproveParticipants(
        workshopId,
        selectedParticipants
      );
      if (result.success) {
        if (result.data?.participants) {
          setParticipants(result.data.participants);
        } else {
          fetchParticipants();
        }
        setSelectedParticipants([]);
      }
    } catch (error) {
alert("Failed to approve participants. Please try again.");
    }
  };

  const handleExportParticipants = async (format) => {
    try {
      await workshopApi.exportParticipants(workshopId, format);
    } catch (error) {
alert("Failed to export participants. Please try again.");
    }
  };

  const toggleParticipantSelection = (userId) => {
    setSelectedParticipants((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllParticipants = () => {
    let participantsToSelect = [];
    if (filter === "pending") {
      participantsToSelect = participants
        .filter((p) => p.status === "pending" && p.status !== "payment_pending")
        .map((p) => p.user._id);
    } else if (filter === "payment_pending") {
      participantsToSelect = participants
        .filter(
          (p) =>
            p.status === "payment_pending" ||
            p.status === "payment-pending" ||
            (p.paymentStatus === "pending" && p.paymentProof)
        )
        .map((p) => p.user._id);
    }
    setSelectedParticipants(participantsToSelect);
  };

  const clearSelection = () => setSelectedParticipants([]);

  const getFilteredParticipants = () => {
    if (filter === "all") return participants;
    return participants.filter((p) => {
      const status = p.status?.toLowerCase();
      const filterLower = filter.toLowerCase();
      if (
        filterLower === "payment_pending" ||
        filterLower === "payment-pending"
      ) {
        const hasPaymentProof = p.paymentProof || p.paymentProofUrl;
        return (
          hasPaymentProof &&
          (status === "payment_pending" ||
            status === "payment-pending" ||
            p.paymentStatus === "pending")
        );
      }
      return status === filterLower;
    });
  };

  const getParticipantCounts = () => {
    const all = participants.length;
    const pending = participants.filter(
      (p) =>
        p.status === "pending" ||
        p.status === "payment_pending" ||
        p.status === "payment-pending"
    ).length;
    const approved = participants.filter((p) => p.status === "approved").length;
    const rejected = participants.filter((p) => p.status === "rejected").length;
    const paymentPending = participants.filter(
      (p) =>
        p.status === "payment_pending" ||
        p.status === "payment-pending" ||
        p.paymentStatus === "pending"
    ).length;
    return { all, pending, approved, rejected, paymentPending };
  };

  const counts = getParticipantCounts();
  const filteredParticipants = getFilteredParticipants();

  // Loading State - Skeleton
  if (loading) {
    return (
      <div className="wpm-container">
        {/* Skeleton Header */}
        <div className="wpm-skeleton-header">
          <div className="wpm-skeleton-header-left">
            <div className="wpm-skeleton wpm-skeleton-icon"></div>
            <div className="wpm-skeleton-header-text">
              <div className="wpm-skeleton wpm-skeleton-title"></div>
              <div className="wpm-skeleton wpm-skeleton-subtitle"></div>
            </div>
          </div>
          <div className="wpm-skeleton-header-actions">
            <div className="wpm-skeleton wpm-skeleton-btn"></div>
            <div className="wpm-skeleton wpm-skeleton-btn"></div>
          </div>
        </div>

        {/* Skeleton Stats */}
        <div className="wpm-skeleton-stats">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="wpm-skeleton wpm-skeleton-stat"></div>
          ))}
        </div>

        {/* Skeleton Filters */}
        <div className="wpm-skeleton-filters">
          <div className="wpm-skeleton wpm-skeleton-search"></div>
          <div className="wpm-skeleton wpm-skeleton-filter"></div>
        </div>

        {/* Skeleton Participant Cards */}
        <div className="wpm-skeleton-list">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="wpm-skeleton-card">
              <div className="wpm-skeleton wpm-skeleton-avatar"></div>
              <div className="wpm-skeleton-card-content">
                <div className="wpm-skeleton wpm-skeleton-name"></div>
                <div className="wpm-skeleton wpm-skeleton-email"></div>
              </div>
              <div className="wpm-skeleton wpm-skeleton-badge"></div>
              <div className="wpm-skeleton wpm-skeleton-actions"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <motion.div
        className="wpm-error"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <p className="wpm-error-text">Failed to load participants: {error}</p>
        <button className="wpm-retry-btn" onClick={fetchParticipants}>
          Try Again
        </button>
      </motion.div>
    );
  }

  return (
    <div className="wpm-container">
      {/* Header */}
      <motion.div
        className="wpm-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="wpm-header-left">
          <div className="wpm-header-icon">
            <FiUsers />
          </div>
          <div>
            <h2 className="wpm-header-title">Participant Management</h2>
            <span className="wpm-header-count">
              {participants.length} total participants
            </span>
          </div>
        </div>
        <div className="wpm-header-actions">
          <button
            className="wpm-export-btn"
            onClick={() => handleExportParticipants("csv")}
            disabled={participants.length === 0}
          >
            <FiDownload />
            CSV
          </button>
          <button
            className="wpm-export-btn"
            onClick={() => handleExportParticipants("excel")}
            disabled={participants.length === 0}
          >
            <FiDownload />
            Excel
          </button>
        </div>
      </motion.div>

      {/* Filter Tabs */}
      <motion.div
        className="wpm-tabs"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <button
          className={`wpm-tab ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All
          <span className="wpm-tab-count">{counts.all}</span>
        </button>
        <button
          className={`wpm-tab ${filter === "pending" ? "active" : ""}`}
          onClick={() => setFilter("pending")}
        >
          Pending
          <span className="wpm-tab-count">{counts.pending}</span>
        </button>
        <button
          className={`wpm-tab ${filter === "payment_pending" ? "active" : ""}`}
          onClick={() => setFilter("payment_pending")}
        >
          Payment Pending
          <span className="wpm-tab-count">{counts.paymentPending}</span>
        </button>
        <button
          className={`wpm-tab ${filter === "approved" ? "active" : ""}`}
          onClick={() => setFilter("approved")}
        >
          Approved
          <span className="wpm-tab-count">{counts.approved}</span>
        </button>
        <button
          className={`wpm-tab ${filter === "rejected" ? "active" : ""}`}
          onClick={() => setFilter("rejected")}
        >
          Rejected
          <span className="wpm-tab-count">{counts.rejected}</span>
        </button>
      </motion.div>

      {/* Bulk Actions - Only show if user can approve */}
      {canApprove && (filter === "pending" || filter === "payment_pending") &&
        (counts.pending > 0 || counts.paymentPending > 0) && (
          <motion.div
            className="wpm-bulk-actions"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="wpm-bulk-left">
              <button className="wpm-bulk-btn" onClick={selectAllParticipants}>
                Select All
              </button>
              <button className="wpm-bulk-btn" onClick={clearSelection}>
                Clear
              </button>
              <span className="wpm-bulk-count">
                {selectedParticipants.length} selected
              </span>
            </div>
            <div className="wpm-bulk-right">
              <button
                className="wpm-approve-all-btn"
                onClick={handleBulkApprove}
                disabled={selectedParticipants.length === 0}
              >
                <FiCheckCircle />
                Approve Selected
              </button>
            </div>
          </motion.div>
        )}

      {/* Participants Grid */}
      <motion.div
        className="wpm-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {filteredParticipants.length > 0 ? (
          filteredParticipants.map((participant) => (
            <ParticipantCard
              key={participant.user._id}
              participant={participant}
              userRole={userRole}
              canApprove={canApprove}
              onApprove={() => handleApprove(participant.user._id)}
              onReject={(reason) => handleReject(participant.user._id, reason)}
              onRemove={(reason) => handleRemove(participant.user._id, reason)}
              onApprovePayment={(message) =>
                handleApprovePayment(participant.user._id, message)
              }
              onRejectPayment={(reason, message) =>
                handleRejectPayment(participant.user._id, reason, message)
              }
              isSelected={selectedParticipants.includes(participant.user._id)}
              onToggleSelection={() =>
                toggleParticipantSelection(participant.user._id)
              }
              showCheckbox={
                filter === "pending" || filter === "payment_pending"
              }
            />
          ))
        ) : (
          <motion.div className="wpm-empty" variants={itemVariants}>
            <div className="wpm-empty-icon">
              <FiUsers />
            </div>
            <h3 className="wpm-empty-title">No participants found</h3>
            <p className="wpm-empty-text">
              {filter === "all"
                ? "No participant applications yet."
                : `No ${filter.replace("_", " ")} participants.`}
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default ParticipantManagement;
